/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Physical Media Collection Page
 * File     : PhysicalMedia.js
 * Author   : Zachary Dry
 * Created  : 2025-01-27
 * Updated  : 2025-01-27
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Physical Media Collection management page for viewing and managing physical media items.
 * -----------------------------------------------------------------------------
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Alert,
  CircularProgress,
  IconButton,

  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Search,
  FilterList,
  Add,
  CloudDownload,
  Close,
  Edit,
  Delete,
  Save,
  Cancel
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../services/api';

const PhysicalMedia = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [format, setFormat] = useState('');
  const [yearInput, setYearInput] = useState('');
  const [year, setYear] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Add Item Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addStep, setAddStep] = useState(0);
  const [addMethod, setAddMethod] = useState('imdb'); // 'imdb' or 'details'
  const [imdbUrl, setImdbUrl] = useState('');
  const [basicDetails, setBasicDetails] = useState({
    title: '',
    year: '',
    format: ''
  });
  const [fetchedMovieData, setFetchedMovieData] = useState(null);
  const [isFetchingMovie, setIsFetchingMovie] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // Reset to first page when search changes
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Debounced year effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setYear(yearInput);
      setPage(1); // Reset to first page when year changes
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [yearInput]);

  // Fetch items when page, search, format, or year changes
  useEffect(() => {
    fetchItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, format, year]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (search) params.append('search', search);
      if (format) params.append('format', format);
      if (year) params.append('year', year);

      const response = await fetch(buildApiUrl(`/api/physical-media?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch physical media items');
      }

      const data = await response.json();
      setItems(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalItems(data.pagination?.total || 0);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setYear(yearInput);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setYearInput('');
    setYear('');
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetch(buildApiUrl(`/api/physical-media/${itemId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Remove item from local state
        setItems(items.filter(item => item.id !== itemId));
        setTotalItems(totalItems - 1);
        
        // Close drawer if the deleted item was selected
        if (selectedItem && selectedItem.id === itemId) {
          setDrawerOpen(false);
          setSelectedItem(null);
        }
      } else {
        throw new Error('Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditItem = () => {
    setIsEditing(true);
    setEditedItem({ ...selectedItem });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedItem(null);
  };

  const handleSaveItem = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(buildApiUrl(`/api/physical-media/${editedItem.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editedItem)
      });

      if (response.ok) {
        const updatedItem = await response.json();
        
        // Update local state
        setItems(items.map(item => 
          item.id === updatedItem.id ? updatedItem : item
        ));
        setSelectedItem(updatedItem);
        setIsEditing(false);
        setEditedItem(null);
      } else {
        throw new Error('Failed to save item');
      }
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save item. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedItem(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // OMDB API integration
  const fetchMovieFromOMDB = async (imdbId = null, title = null, year = null) => {
    try {
      setIsFetchingMovie(true);
      setFetchError(null);
      
      let url = buildApiUrl('/api/movies');
      
      if (imdbId) {
        // Extract IMDb ID from URL if provided
        const imdbMatch = imdbId.match(/tt\d+/);
        const extractedId = imdbMatch ? imdbMatch[0] : imdbId;
        url += `/${extractedId}`;
      } else if (title && year) {
        url += `/by-title?title=${encodeURIComponent(title)}&year=${year}`;
      } else {
        throw new Error('Either IMDb ID or title and year must be provided');
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch movie data: ${response.status}`);
      }
      
      const movieData = await response.json();
      
      // Transform OMDB data to our format
      const transformedData = {
        title: movieData.Title || '',
        year: parseInt(movieData.Year) || null,
        genre: movieData.Genre || '',
        director: movieData.Director || '',
        plot: movieData.Plot || '',
        runtime: movieData.Runtime ? parseInt(movieData.Runtime.replace(/\D/g, '')) : null,
        rating: movieData.Rated || '',
        imdbId: movieData.imdbID || '',
        cast: movieData.Actors || '',
        posterUrl: movieData.Poster && movieData.Poster !== 'N/A' ? movieData.Poster : null,
        backdropUrl: null, // OMDB doesn't provide backdrop
        format: basicDetails.format || 'other',
        barcode: '',
        datePurchased: null,
        purchasePrice: null,
        locationPurchased: '',
        isWatched: false,
        watchDate: null,
        distributor: '',
        edition: '',
        audioTracks: '',
        subtitles: '',
        regions: '',
        packaging: '',
        notes: ''
      };
      
      setFetchedMovieData(transformedData);
      setAddStep(2); // Move to confirmation step
      
    } catch (error) {
      console.error('Error fetching movie data:', error);
      setFetchError(error.message);
    } finally {
      setIsFetchingMovie(false);
    }
  };

  // Add Item Modal handlers
  const handleAddItem = () => {
    setAddModalOpen(true);
    setAddStep(0);
    setAddMethod('imdb');
    setImdbUrl('');
    setBasicDetails({ title: '', year: '', format: '' });
    setFetchedMovieData(null);
    setFetchError(null);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setAddStep(0);
    setFetchedMovieData(null);
    setFetchError(null);
  };

  const handleNextStep = () => {
    if (addStep === 0) {
      setAddStep(1);
    } else if (addStep === 1) {
      // Fetch movie data based on method
      if (addMethod === 'imdb' && imdbUrl.trim()) {
        fetchMovieFromOMDB(imdbUrl);
      } else if (addMethod === 'details' && basicDetails.title.trim() && basicDetails.year) {
        fetchMovieFromOMDB(null, basicDetails.title, basicDetails.year);
      }
    }
  };

  const handleConfirmDetails = () => {
    // Close modal and open drawer with fetched data
    setAddModalOpen(false);
    setSelectedItem(fetchedMovieData);
    setDrawerOpen(true);
    setIsEditing(true);
    setEditedItem({ ...fetchedMovieData });
  };

  const renderField = (label, value, field, type = 'text', options = null) => {
    const currentValue = isEditing ? (editedItem?.[field] || '') : (value || '');
    
    if (isEditing) {
      if (type === 'select' && options) {
        return (
          <TextField
            select
            fullWidth
            size="small"
            value={currentValue}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            variant="outlined"
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      } else if (type === 'multiline') {
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            value={currentValue}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            variant="outlined"
          />
        );
      } else {
        return (
          <TextField
            fullWidth
            size="small"
            type={type}
            value={currentValue}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            variant="outlined"
          />
        );
      }
    } else {
      if (field === 'format') {
        return (
          <Chip 
            label={formatFormat(value)} 
            color={getFormatColor(value)}
            size="small"
          />
        );
      }
      if (field === 'coverFront' || field === 'coverBack') {
        if (value && value.startsWith('data:image/')) {
          return (
            <Typography variant="body1">
              <a 
                href={value} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'underline' }}
              >
                View Image
              </a>
            </Typography>
          );
        }
        return <Typography variant="body1">N/A</Typography>;
      }
      return <Typography variant="body1">{value || 'N/A'}</Typography>;
    }
  };

  const getFormatColor = (format) => {
    const colors = {
      'bluray': 'primary',
      '4k': 'secondary',
      'dvd': 'success',
      'vhs': 'warning',
      'laserdisc': 'info',
      'other': 'default'
    };
    return colors[format] || 'default';
  };

  const formatFormat = (format) => {
    const formats = {
      'bluray': 'Blu-ray',
      '4k': '4K UHD',
      'dvd': 'DVD',
      'vhs': 'VHS',
      'laserdisc': 'Laserdisc',
      'other': 'Other'
    };
    return formats[format] || format;
  };

  if (loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Physical Media Collection
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {user?.role === 'owner' && (
            <Button
              variant="contained"
              startIcon={<CloudDownload />}
              onClick={() => window.location.href = '/clz-migration'}
            >
              CLZ Migration
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddItem}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Format</InputLabel>
                <Select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  label="Format"
                >
                  <MenuItem value="">All Formats</MenuItem>
                  <MenuItem value="bluray">Blu-ray</MenuItem>
                  <MenuItem value="4k">4K UHD</MenuItem>
                  <MenuItem value="dvd">DVD</MenuItem>
                  <MenuItem value="vhs">VHS</MenuItem>
                  <MenuItem value="laserdisc">Laserdisc</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={yearInput}
                onChange={(e) => setYearInput(e.target.value)}
                inputProps={{ min: 1900, max: 2030 }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              {searchInput ? (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={clearSearch}
                  startIcon={<Close />}
                >
                  Clear
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<FilterList />}
                >
                  Filter
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {items.length} of {totalItems} items
      </Typography>

      {/* Items List */}
      {items.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No physical media items found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {user?.role === 'owner' ? 'Start by migrating your CLZ collection or adding items manually.' : 'No items in your collection yet.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Paper>
          <List>
            {items.map((item) => (
              <ListItem 
                key={item.id} 
                divider 
                button 
                onClick={() => {
                  setSelectedItem(item);
                  setDrawerOpen(true);
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteItem(item.id);
                    }}
                    disabled={isDeleting}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar 
                    variant="rounded" 
                    src={item.coverFront || undefined}
                    alt={`${item.title} cover`}
                    sx={{ width: 56, height: 84 }}
                  >
                    {item.title?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6" component="span">
                        {item.title}
                      </Typography>
                      {item.year && (
                        <Typography variant="body2" color="text.secondary" component="span">
                          ({item.year})
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {item.director && `${item.director} • `}{item.genre}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip 
                          label={formatFormat(item.format)} 
                          size="small" 
                          color={getFormatColor(item.format)}
                        />
                        {item.imdbRating && (
                          <Chip 
                            label={`IMDb: ${item.imdbRating}`} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                        {item.barcode && (
                          <Typography variant="caption" color="text.secondary">
                            Barcode: {item.barcode}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Detail Drawer */}
      <Drawer 
        anchor="right" 
        open={drawerOpen} 
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 500 },
            p: 2,
            top: { xs: '56px', sm: '64px' },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' }
          }
        }}
      >
        {selectedItem && (
          <Box>
            <Box sx={{ position: 'sticky', top: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">
                {selectedItem.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!isEditing ? (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    size="small"
                    onClick={handleEditItem}
                  >
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      size="small"
                      onClick={handleSaveItem}
                      disabled={isSaving}
                      color="primary"
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      size="small"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                  </>
                )}
                <IconButton aria-label="close" onClick={() => {
                  setDrawerOpen(false);
                  setIsEditing(false);
                  setEditedItem(null);
                }} size="small">
                  <Close />
                </IconButton>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Cover Images */}
            {(selectedItem.coverFront || selectedItem.coverBack) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                {selectedItem.coverFront && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Front Cover
                    </Typography>
                    <img
                      src={selectedItem.coverFront}
                      alt={`${selectedItem.title} - Front Cover`}
                      style={{ 
                        maxWidth: '200px', 
                        maxHeight: '300px', 
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  </Box>
                )}
                {selectedItem.coverBack && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Back Cover
                    </Typography>
                    <img
                      src={selectedItem.coverBack}
                      alt={`${selectedItem.title} - Back Cover`}
                      style={{ 
                        maxWidth: '200px', 
                        maxHeight: '300px', 
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Basic Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                  {renderField('Title', selectedItem.title, 'title')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Year</Typography>
                  {renderField('Year', selectedItem.year, 'year', 'number')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Format</Typography>
                  {renderField('Format', selectedItem.format, 'format', 'select', [
                    { value: 'dvd', label: 'DVD' },
                    { value: 'bluray', label: 'Blu-ray' },
                    { value: '4k', label: '4K UHD' },
                    { value: 'vhs', label: 'VHS' },
                    { value: 'laserdisc', label: 'Laserdisc' },
                    { value: 'other', label: 'Other' }
                  ])}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Genre</Typography>
                  {renderField('Genre', selectedItem.genre, 'genre')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Director</Typography>
                  {renderField('Director', selectedItem.director, 'director')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Plot</Typography>
                  {renderField('Plot', selectedItem.plot, 'plot', 'multiline')}
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Technical Details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Technical Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Runtime (minutes)</Typography>
                  {renderField('Runtime', selectedItem.runtime, 'runtime', 'number')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Rating</Typography>
                  {renderField('Rating', selectedItem.rating, 'rating')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">IMDb Rating</Typography>
                  {renderField('IMDb Rating', selectedItem.imdbRating, 'imdbRating', 'number')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">IMDb Votes</Typography>
                  {renderField('IMDb Votes', selectedItem.imdbVotes, 'imdbVotes', 'number')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Barcode</Typography>
                  {renderField('Barcode', selectedItem.barcode, 'barcode')}
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Collection Details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Collection Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Distributor</Typography>
                  {renderField('Distributor', selectedItem.distributor, 'distributor')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Edition</Typography>
                  {renderField('Edition', selectedItem.edition, 'edition')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Purchase Date</Typography>
                  {renderField('Purchase Date', selectedItem.purchaseDate, 'purchaseDate', 'date')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Purchase Price</Typography>
                  {renderField('Purchase Price', selectedItem.purchasePrice, 'purchasePrice', 'number')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Purchase Location</Typography>
                  {renderField('Purchase Location', selectedItem.purchaseLocation, 'purchaseLocation')}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Watched</Typography>
                  {isEditing ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={editedItem?.isWatched ? 'true' : 'false'}
                      onChange={(e) => handleFieldChange('isWatched', e.target.value === 'true')}
                      variant="outlined"
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </TextField>
                  ) : (
                    <Chip 
                      label={selectedItem.isWatched ? 'Yes' : 'No'} 
                      color={selectedItem.isWatched ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Watch Date</Typography>
                  {renderField('Watch Date', selectedItem.watchDate, 'watchDate', 'date')}
                </Grid>
              </Grid>
            </Box>

            {/* Additional Information */}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Additional Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Audio Tracks</Typography>
                  {renderField('Audio Tracks', selectedItem.audioTracks, 'audioTracks')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Subtitles</Typography>
                  {renderField('Subtitles', selectedItem.subtitles, 'subtitles')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Regions</Typography>
                  {renderField('Regions', selectedItem.regions, 'regions')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Packaging</Typography>
                  {renderField('Packaging', selectedItem.packaging, 'packaging')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Front Cover</Typography>
                  {renderField('Front Cover', selectedItem.coverFront, 'coverFront')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Back Cover</Typography>
                  {renderField('Back Cover', selectedItem.coverBack, 'coverBack')}
                </Grid>
              </Grid>
            </Box>

            {/* Cast & Crew */}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Cast & Crew</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Cast</Typography>
                  {renderField('Cast', selectedItem.cast, 'cast', 'multiline')}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Crew</Typography>
                  {renderField('Crew', selectedItem.crew, 'crew', 'multiline')}
                </Grid>
              </Grid>
            </Box>

            {/* Notes */}
            <Divider sx={{ mb: 2 }} />
            <Box>
              <Typography variant="h6" gutterBottom>Notes</Typography>
              {renderField('Notes', selectedItem.notes, 'notes', 'multiline')}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Add Item Modal */}
      <Dialog open={addModalOpen} onClose={handleCloseAddModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Add color="primary" />
            Add New Movie
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={addStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Choose Method</StepLabel>
            </Step>
            <Step>
              <StepLabel>Enter Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirm Details</StepLabel>
            </Step>
          </Stepper>

          {addStep === 0 && (
            <Box>
              <FormLabel component="legend" sx={{ mb: 2 }}>
                How would you like to add the movie?
              </FormLabel>
              <RadioGroup
                value={addMethod}
                onChange={(e) => setAddMethod(e.target.value)}
              >
                <FormControlLabel
                  value="imdb"
                  control={<Radio />}
                  label="Enter IMDb URL"
                />
                <FormControlLabel
                  value="details"
                  control={<Radio />}
                  label="Enter basic movie details"
                />
              </RadioGroup>
            </Box>
          )}

          {addStep === 1 && (
            <Box>
              {addMethod === 'imdb' ? (
                <TextField
                  fullWidth
                  label="IMDb URL"
                  value={imdbUrl}
                  onChange={(e) => setImdbUrl(e.target.value)}
                  placeholder="https://www.imdb.com/title/tt1234567/"
                  helperText="Enter the full IMDb URL for the movie"
                  sx={{ mb: 2 }}
                />
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Movie Title"
                      value={basicDetails.title}
                      onChange={(e) => setBasicDetails(prev => ({ ...prev, title: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Year"
                      type="number"
                      value={basicDetails.year}
                      onChange={(e) => setBasicDetails(prev => ({ ...prev, year: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Format (Optional)</InputLabel>
                      <Select
                        value={basicDetails.format}
                        onChange={(e) => setBasicDetails(prev => ({ ...prev, format: e.target.value }))}
                        label="Format (Optional)"
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="bluray">Blu-ray</MenuItem>
                        <MenuItem value="4k">4K UHD</MenuItem>
                        <MenuItem value="dvd">DVD</MenuItem>
                        <MenuItem value="vhs">VHS</MenuItem>
                        <MenuItem value="laserdisc">Laserdisc</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {addStep === 2 && fetchedMovieData && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Do these details look correct?
              </Typography>
              
              {fetchError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {fetchError}
                </Alert>
              )}

              {isFetchingMovie ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Fetching movie details...</Typography>
                </Box>
              ) : (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        {fetchedMovieData.posterUrl && (
                          <img
                            src={fetchedMovieData.posterUrl}
                            alt={fetchedMovieData.title}
                            style={{ width: '100%', maxWidth: '200px', borderRadius: '8px' }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Typography variant="h6" gutterBottom>
                          {fetchedMovieData.title} ({fetchedMovieData.year})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Director:</strong> {fetchedMovieData.director || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Genre:</strong> {fetchedMovieData.genre || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Runtime:</strong> {fetchedMovieData.runtime ? `${fetchedMovieData.runtime} min` : 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Rating:</strong> {fetchedMovieData.rating || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Format:</strong> {formatFormat(fetchedMovieData.format)}
                        </Typography>
                        {fetchedMovieData.plot && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {fetchedMovieData.plot}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal}>
            Cancel
          </Button>
          {addStep === 0 && (
            <Button onClick={handleNextStep} variant="contained">
              Next
            </Button>
          )}
          {addStep === 1 && (
            <Button 
              onClick={handleNextStep} 
              variant="contained"
              disabled={
                (addMethod === 'imdb' && !imdbUrl.trim()) ||
                (addMethod === 'details' && (!basicDetails.title.trim() || !basicDetails.year))
              }
            >
              {isFetchingMovie ? <CircularProgress size={20} /> : 'Fetch Details'}
            </Button>
          )}
          {addStep === 2 && !isFetchingMovie && !fetchError && (
            <Button onClick={handleConfirmDetails} variant="contained">
              Yes, Add to Collection
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhysicalMedia;
